// ============================================================================
// useTomLive — MS-07 (read-only) + auto-refresh + TTL cache
// ----------------------------------------------------------------------------
// Lê dados ao vivo de Theory of Mind:
//   • Últimos turn inferences (intent + urgency + vulnerability)
//   • Top membros modelados (tom_models)
//   • Últimas contribuições (with_tom vs without_tom — A/B judging)
//   • Volumes 24h por tabela ToM
//
// Recursos adicionais:
//   • Cache TTL em memória (module-level) → evita re-fetch ao trocar de aba.
//   • Polling configurável via `intervalMs` (0 = off).
//   • Pausa polling quando document.hidden (aba em background).
//   • `lastUpdatedAt` exposto para UI mostrar freshness.
// ============================================================================

import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface TomTurnInference {
  id: string;
  group_id: string | null;
  member_jid: string | null;
  primary_intent: string | null;
  urgency: number | null;
  vulnerability: number | null;
  openness_to_challenge: number | null;
  confidence_parsing: number | null;
  created_at: string;
}

export interface TomModelRow {
  id: string;
  member_jid: string | null;
  display_name: string | null;
  group_id: string | null;
  formality: number | null;
  verbosity: number | null;
  directness: number | null;
  assertiveness: number | null;
  observation_count: number | null;
  effective_observations: number | null;
  last_intent: string | null;
  updated_at: string;
}

export interface TomContributionRow {
  id: string;
  group_id: string | null;
  with_tom_score: number | null;
  without_tom_score: number | null;
  contribution_score: number | null;
  winner: string | null;
  judge_model: string | null;
  created_at: string;
}

export interface TomVolume {
  table: string;
  total: number;
  last_24h: number;
}

export interface TomLiveData {
  inferences: TomTurnInference[];
  models: TomModelRow[];
  contributions: TomContributionRow[];
  volumes: TomVolume[];
  loading: boolean;
  error: string | null;
  lastUpdatedAt: Date | null;
  refetch: (opts?: { force?: boolean }) => Promise<void>;
}

interface TomCacheSnapshot {
  inferences: TomTurnInference[];
  models: TomModelRow[];
  contributions: TomContributionRow[];
  volumes: TomVolume[];
  fetchedAt: number;
}

// ── Module-level cache (compartilhado entre montagens do hook) ────────────
let cache: TomCacheSnapshot | null = null;
let inflight: Promise<TomCacheSnapshot> | null = null;

// TTL default do cache: usado tanto para hidratação inicial quanto para
// short-circuitar refetchs frequentes (ex.: polling agressivo).
const DEFAULT_CACHE_TTL_MS = 15_000;

async function fetchTomSnapshot(): Promise<TomCacheSnapshot> {
  const since = new Date(Date.now() - 24 * 3600_000).toISOString();

  const [infRes, modRes, contRes, vInf, vMod, vCont, vIntent] = await Promise.all([
    supabase
      .from("tom_turn_inferences")
      .select(
        "id,group_id,member_jid,primary_intent,urgency,vulnerability,openness_to_challenge,confidence_parsing,created_at",
      )
      .order("created_at", { ascending: false })
      .limit(20),
    supabase
      .from("tom_models")
      .select(
        "id,member_jid,display_name,group_id,formality,verbosity,directness,assertiveness,observation_count,effective_observations,last_intent,updated_at",
      )
      .order("observation_count", { ascending: false })
      .limit(15),
    supabase
      .from("tom_contribution_runs")
      .select(
        "id,group_id,with_tom_score,without_tom_score,contribution_score,winner,judge_model,created_at",
      )
      .order("created_at", { ascending: false })
      .limit(10),
    supabase.from("tom_turn_inferences").select("id", { count: "exact", head: true }),
    supabase.from("tom_models").select("id", { count: "exact", head: true }),
    supabase
      .from("tom_contribution_runs")
      .select("id", { count: "exact", head: true })
      .gte("created_at", since),
    supabase.from("intent_events").select("id", { count: "exact", head: true }).gte("created_at", since),
  ]);

  if (infRes.error) throw infRes.error;
  if (modRes.error) throw modRes.error;
  if (contRes.error) throw contRes.error;

  // Volumes 24h específicos
  const inf24 = await supabase
    .from("tom_turn_inferences")
    .select("id", { count: "exact", head: true })
    .gte("created_at", since);
  const mod24 = await supabase
    .from("tom_models")
    .select("id", { count: "exact", head: true })
    .gte("updated_at", since);

  return {
    inferences: (infRes.data ?? []) as TomTurnInference[],
    models: (modRes.data ?? []) as TomModelRow[],
    contributions: (contRes.data ?? []) as TomContributionRow[],
    volumes: [
      { table: "tom_turn_inferences", total: vInf.count ?? 0, last_24h: inf24.count ?? 0 },
      { table: "tom_models", total: vMod.count ?? 0, last_24h: mod24.count ?? 0 },
      { table: "tom_contribution_runs", total: 0, last_24h: vCont.count ?? 0 },
      { table: "intent_events", total: 0, last_24h: vIntent.count ?? 0 },
    ],
    fetchedAt: Date.now(),
  };
}

/**
 * @param intervalMs Intervalo de polling. 0 desliga polling automático.
 * @param cacheTtlMs TTL do cache; fetchs dentro do TTL são short-circuit.
 */
export function useTomLive(intervalMs = 0, cacheTtlMs = DEFAULT_CACHE_TTL_MS): TomLiveData {
  const [snapshot, setSnapshot] = useState<TomCacheSnapshot | null>(cache);
  const [loading, setLoading] = useState(!cache);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const refetch = useCallback(
    async (opts?: { force?: boolean }) => {
      const force = opts?.force === true;
      // Short-circuit: cache ainda fresco e não-forçado.
      if (!force && cache && Date.now() - cache.fetchedAt < cacheTtlMs) {
        if (mounted.current) setSnapshot(cache);
        return;
      }
      // Coalesce concurrent calls (várias instâncias do hook).
      if (!inflight) {
        inflight = fetchTomSnapshot()
          .then((snap) => {
            cache = snap;
            return snap;
          })
          .finally(() => {
            inflight = null;
          });
      }
      if (mounted.current) {
        setLoading(!cache); // só "loading" quando não há nada para mostrar
        setError(null);
      }
      try {
        const snap = await inflight;
        if (mounted.current) setSnapshot(snap);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (mounted.current) setError(msg);
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    [cacheTtlMs],
  );

  // Mount: hidrata do cache e faz fetch (respeitando TTL).
  useEffect(() => {
    mounted.current = true;
    refetch();
    return () => {
      mounted.current = false;
    };
  }, [refetch]);

  // Polling configurável + pausa quando aba está em background.
  useEffect(() => {
    if (!intervalMs || intervalMs <= 0) return;
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (typeof document !== "undefined" && document.hidden) return;
        // Polling sempre tenta refetch; cache TTL ainda protege contra
        // intervalos muito agressivos.
        refetch();
      }, intervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Ao voltar, faz um refetch imediato (respeita TTL) e reinicia timer.
        refetch();
        start();
      }
    };

    start();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [intervalMs, refetch]);

  return {
    inferences: snapshot?.inferences ?? [],
    models: snapshot?.models ?? [],
    contributions: snapshot?.contributions ?? [],
    volumes: snapshot?.volumes ?? [],
    loading,
    error,
    lastUpdatedAt: snapshot ? new Date(snapshot.fetchedAt) : null,
    refetch,
  };
}
